import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2, AlertCircle } from "lucide-react";
import { http, eur } from "@/lib/api";
import { EscapadeCard } from "@/components/EscapadeCard";
import { GeneratingState } from "@/components/GeneratingState";
import { themeLabel } from "@/components/ThemePicker";

export default function Results() {
  const { searchId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let stop = false;
    const poll = async () => {
      try {
        const { data: d } = await http.get(`/search/${searchId}`);
        if (stop) return;
        setData(d);
        if (d.status === "pending") setTimeout(poll, 5000);
      } catch { if (!stop) setError("Recherche introuvable."); }
    };
    poll();
    return () => { stop = true; };
  }, [searchId]);

  if (error) return <div data-testid="results-error" className="max-w-7xl mx-auto px-4 py-24 text-center text-gray-600">{error} <Link to="/" className="text-amber-700 underline">Nouvelle recherche</Link></div>;
  if (!data) return <div className="py-32 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-600" /></div>;

  const q = data.query;
  if (data.status === "pending") return <div className="bg-[#0B131E] py-20 px-4"><div className="max-w-3xl mx-auto"><GeneratingState city={q.departure_city} /></div></div>;
  if (data.status === "failed") return (
    <div data-testid="results-failed" className="max-w-xl mx-auto px-4 py-24 text-center">
      <AlertCircle className="w-8 h-8 text-amber-600 mx-auto" />
      <p className="mt-4 text-gray-700">{data.error}</p>
      <Link to="/" data-testid="results-retry-link" className="mt-6 inline-flex h-11 px-6 items-center rounded-full bg-[#0B131E] text-white text-sm font-semibold hover:bg-amber-600 transition-colors">Modifier ma recherche</Link>
    </div>
  );

  const escs = data.escapades.map((e) => ({ ...e, user_budget: e.user_budget || q.budget }));
  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-14">
      <Link to="/" data-testid="results-new-search-link" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-amber-700 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Nouvelle recherche
      </Link>
      <div className="mt-6 flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-amber-600">3 escapades recommandées</p>
          <h1 className="mt-2 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B131E]">Au départ de {q.departure_city}</h1>
        </div>
        <div data-testid="results-query-summary" className="flex flex-wrap gap-2 text-sm">
          {[eur(q.budget) + " max", `${q.duration} jour${q.duration > 1 ? "s" : ""}`, `${q.travelers} voyageur${q.travelers > 1 ? "s" : ""}`, q.profile, q.theme && `Thème : ${themeLabel(q.theme)}`].filter(Boolean).map((t) => (
            <span key={t} className="px-3 py-1.5 rounded-full bg-white border border-gray-200 text-gray-700 first-letter:uppercase">{t}</span>
          ))}
        </div>
      </div>
      <div data-testid="results-grid" className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-8">
        {escs.map((e, i) => <EscapadeCard key={e.id} e={e} index={i} />)}
      </div>
      <p className="mt-10 text-xs text-gray-500 max-w-2xl">Estimations indicatives basées sur des tarifs moyens récents. Vérifiez disponibilités et prix avant de réserver.</p>
    </div>
  );
}
